import React, { useCallback, useEffect, useState } from 'react';
import { Translate, getSortState, translate } from 'react-jhipster';
import { RouteComponentProps } from 'react-router-dom';

import { DeleteRounded, EditRounded } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { TableComponent } from 'app/shared/components/table-component/TableComponent';
import { IHeaderItem } from 'app/shared/interfaces/headerItemsInterface';
import { IListData } from 'app/shared/interfaces/listDataInterface';
import { ISearchTypes } from 'app/shared/interfaces/searchTypesInterface';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { Loading } from 'app/shared/util/loading';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC } from 'app/shared/util/pagination.constants';
import { getEntities } from './inspection.reducer';

export const Inspection = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'id'), props.location.search)
  );
  const urlParams = new URLSearchParams(props.location.search);

  const inspectionList = useAppSelector(state => state.inspection.entities);
  const loading = useAppSelector(state => state.inspection.loading);
  const totalItems = useAppSelector(state => state.inspection.totalItems);
  const [nameSearch, setNameSearch] = useState(urlParams.get('nameSearch') || '');
  const [inspectionId, setInspectionId] = useState(urlParams.get('inspectionId') || '');

  const groupButtonIcons = [
    {
      icon: <EditRounded color="secondary" />,
      path: 'edit',
    },
    {
      icon: <DeleteRounded color="error" />,
      path: 'delete',
    },
  ];

  const headers: IHeaderItem[] = [
    { key: 'name', sortable: true, component: <Translate contentKey="fleetSenseApp.inspection.name" /> },
    { key: 'description', sortable: true, component: <Translate contentKey="fleetSenseApp.inspection.description" /> },
    { key: 'type', sortable: true, component: <Translate contentKey="fleetSenseApp.inspection.type" /> },
  ];

  const data: IListData[] = [
    { type: 'text', value: 'name' },
    { type: 'text', value: 'description' },
    { type: 'translateText', value: 'type', translate: 'fleetSenseApp.InspectionTypes' },
    { type: 'groupButtonIcons', value: 'id' },
  ];

  const getAllEntities = useCallback(() => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        nameSearch,
        inspectionId: inspectionId ? +inspectionId : null,
      })
    );
  }, [paginationState]);

  const sortEntities = () => {
    const searchParams = [];
    if (nameSearch !== '') {
      searchParams.push(`nameSearch=${encodeURIComponent(nameSearch)}`);
    }
    if (inspectionId !== '') {
      searchParams.push(`inspectionId=${encodeURIComponent(inspectionId)}`);
    }

    const searchQueryString = searchParams.length > 0 ? `&${searchParams.join('&')}` : '';

    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}${searchQueryString}`;

    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const setActivePage = () => {
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  useEffect(() => {
    getAllEntities();
  }, [getAllEntities]);

  const setNameSearchPageHeader = () => {
    if (inspectionId && inspectionList?.length) {
      return inspectionList?.find(inspection => inspection.id === +inspectionId)?.name;
    }
    return nameSearch;
  };

  const searchTypes: ISearchTypes[] = [
    {
      label: translate('fleetSenseApp.inspection.searchLabel'),
      placeholder: translate('fleetSenseApp.inspection.search'),
      value: setNameSearchPageHeader().toString(),
      setValue: inspectionId ? setInspectionId : setNameSearch,
    },
  ];

  const { match } = props;

  return (
    <Box sx={{ backgroundColor: 'primary.main', borderRadius: '8px', paddingTop: '16px', textAlign: 'center', padding: '24px' }}>
      <PageHeader
        headingId="inspection-heading"
        headingDataCy="inspectionHeading"
        contentKey="fleetSenseApp.inspection.home.title"
        buttonTextKey="fleetSenseApp.inspection.home.createLabel"
        match={match}
      />
      <PageFilters setActivePage={setActivePage} searchTypes={searchTypes} showFilters={['filters']} />
      {!loading && !!inspectionList?.length && (
        <TableComponent
          listData={data}
          headers={headers}
          list={inspectionList}
          match={match}
          sortFunction={sort}
          pagination={paginationState}
          groupButtonIcons={groupButtonIcons}
        />
      )}
      {!loading && !inspectionList.length && <NoData />}
      {loading && <Loading />}
      <PaginationComponent paginationState={paginationState} totalItems={totalItems} onChange={handlePagination} list={inspectionList} />
    </Box>
  );
};

export default Inspection;
